import * as THREE from 'three';

export interface CameraView {
  id: number; // 1..6
  nameKey: string;
  position: THREE.Vector3;
  target: THREE.Vector3;
  mode: 'orbit' | 'fly' | 'boat';
  minDistance?: number;
  maxDistance?: number;
  fov?: number;
}

export class CameraRig {
  public camera: THREE.PerspectiveCamera;
  private canvas: HTMLCanvasElement;

  // View state
  public currentMode: 'orbit' | 'fly' | 'boat' | 'transition' = 'orbit';
  public target: THREE.Vector3 = new THREE.Vector3(0, 0, 0);
  private desiredTarget: THREE.Vector3 = new THREE.Vector3(0, 0, 0);
  private desiredPosition: THREE.Vector3 = new THREE.Vector3(0, 10, 20);

  // Orbit spherical coordinates
  private spherical: THREE.Spherical = new THREE.Spherical(25, Math.PI / 3, Math.PI / 4);
  private targetSpherical: THREE.Spherical = new THREE.Spherical(25, Math.PI / 3, Math.PI / 4);
  private minDistance: number = 4;
  private maxDistance: number = 180;
  private minPolarAngle: number = 0.05;
  private maxPolarAngle: number = Math.PI / 2 - 0.02; // Keep strictly above ground

  // Transition interpolation
  private transitionStartPos: THREE.Vector3 = new THREE.Vector3();
  private transitionEndPos: THREE.Vector3 = new THREE.Vector3();
  private transitionStartTarget: THREE.Vector3 = new THREE.Vector3();
  private transitionEndTarget: THREE.Vector3 = new THREE.Vector3();
  private transitionProgress: number = 1.0;
  private transitionDuration: number = 1.2;

  // Fly / Boat movement
  public flyMoveVector: THREE.Vector3 = new THREE.Vector3();
  public boatMoveVector: THREE.Vector2 = new THREE.Vector2(); // x: turn, y: forward/back
  private flySpeed: number = 24.0;
  private flyEuler: THREE.Euler = new THREE.Euler(0, 0, 0, 'YXZ');

  // Interaction pointers
  private isPointerDown: boolean = false;
  private pointerButton: number = 0; // 0 left, 2 right
  private lastPointerX: number = 0;
  private lastPointerY: number = 0;
  private activePointers: Map<number, { x: number; y: number }> = new Map();
  private lastTouchDistance: number = 0;
  private lastTapTime: number = 0;

  // Auto-tour / Chill mode
  public isChillMode: boolean = false;
  private chillAngle: number = 0;
  private chillSpeed: number = 0.05;
  public reduceMotion: boolean = false;

  // Callback when user manually interacts to cancel chill mode
  public onUserInteracted?: () => void;

  constructor(canvas: HTMLCanvasElement, aspect: number) {
    this.canvas = canvas;
    this.camera = new THREE.PerspectiveCamera(48, aspect, 0.5, 900);
    this.camera.position.set(0, 12, 28);
    this.camera.lookAt(this.target);

    this.setupListeners();
  }

  private setupListeners(): void {
    const el = this.canvas;

    // Pointer events
    el.addEventListener('pointerdown', this.onPointerDown, { passive: false });
    window.addEventListener('pointermove', this.onPointerMove, { passive: false });
    window.addEventListener('pointerup', this.onPointerUp);
    window.addEventListener('pointercancel', this.onPointerUp);

    // Wheel
    el.addEventListener('wheel', this.onWheel, { passive: false });

    // Prevent context menu on canvas
    el.addEventListener('contextmenu', (e) => e.preventDefault());
  }

  public setView(view: CameraView, immediate: boolean = false): void {
    this.currentMode = view.mode;
    this.desiredPosition.copy(view.position);
    this.desiredTarget.copy(view.target);

    if (view.minDistance) this.minDistance = view.minDistance;
    if (view.maxDistance) this.maxDistance = view.maxDistance;

    if (immediate) {
      this.camera.position.copy(view.position);
      this.target.copy(view.target);
      this.camera.lookAt(this.target);
      this.updateSphericalFromCurrent();
      this.transitionProgress = 1.0;
    } else {
      this.transitionStartPos.copy(this.camera.position);
      this.transitionEndPos.copy(view.position);
      this.transitionStartTarget.copy(this.target);
      this.transitionEndTarget.copy(view.target);
      this.transitionProgress = 0.0;
    }
  }

  private updateSphericalFromCurrent(): void {
    const offset = new THREE.Vector3().subVectors(this.camera.position, this.target);
    this.spherical.setFromVector3(offset);
    this.targetSpherical.copy(this.spherical);
  }

  private onPointerDown = (e: PointerEvent): void => {
    e.preventDefault();
    this.canvas.setPointerCapture(e.pointerId);
    this.activePointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

    this.isPointerDown = true;
    this.pointerButton = e.button;
    this.lastPointerX = e.clientX;
    this.lastPointerY = e.clientY;

    if (this.isChillMode) {
      this.isChillMode = false;
      this.onUserInteracted?.();
    }

    // Double tap detector
    const now = performance.now();
    if (now - this.lastTapTime < 300) {
      this.resetView();
    }
    this.lastTapTime = now;

    if (this.activePointers.size === 2) {
      const pts = Array.from(this.activePointers.values());
      const dx = pts[0].x - pts[1].x;
      const dy = pts[0].y - pts[1].y;
      this.lastTouchDistance = Math.hypot(dx, dy);
    }
  };

  private onPointerMove = (e: PointerEvent): void => {
    if (!this.activePointers.has(e.pointerId)) return;
    this.activePointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (this.activePointers.size === 1) {
      const dx = e.clientX - this.lastPointerX;
      const dy = e.clientY - this.lastPointerY;
      this.lastPointerX = e.clientX;
      this.lastPointerY = e.clientY;

      if (this.pointerButton === 2) {
        // Pan
        this.pan(dx, dy);
      } else {
        // Orbit / Look
        if (this.currentMode === 'orbit') {
          this.targetSpherical.theta -= dx * 0.005;
          this.targetSpherical.phi -= dy * 0.005;
          this.targetSpherical.phi = Math.max(this.minPolarAngle, Math.min(this.maxPolarAngle, this.targetSpherical.phi));
        } else if (this.currentMode === 'fly') {
          this.flyEuler.setFromQuaternion(this.camera.quaternion);
          this.flyEuler.y -= dx * 0.003;
          this.flyEuler.x -= dy * 0.003;
          this.flyEuler.x = Math.max(-Math.PI / 2.5, Math.min(Math.PI / 2.5, this.flyEuler.x));
          this.camera.quaternion.setFromEuler(this.flyEuler);
        }
      }
    } else if (this.activePointers.size === 2) {
      // 2-finger gestures: pinch zoom and two-finger pan
      const pts = Array.from(this.activePointers.values());
      const currentDist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
      if (this.lastTouchDistance > 0) {
        const pinchDelta = currentDist - this.lastTouchDistance;
        this.zoom(pinchDelta * 0.05);
      }
      this.lastTouchDistance = currentDist;
    }
  };

  private onPointerUp = (e: PointerEvent): void => {
    this.activePointers.delete(e.pointerId);
    if (this.activePointers.size === 0) {
      this.isPointerDown = false;
      this.lastTouchDistance = 0;
    }
  };

  private onWheel = (e: WheelEvent): void => {
    e.preventDefault();
    if (this.isChillMode) {
      this.isChillMode = false;
      this.onUserInteracted?.();
    }
    const delta = e.deltaY * 0.02;
    this.zoom(-delta);
  };

  private zoom(amount: number): void {
    if (this.currentMode === 'orbit') {
      this.targetSpherical.radius = THREE.MathUtils.clamp(
        this.targetSpherical.radius - amount,
        this.minDistance,
        this.maxDistance
      );
    } else if (this.currentMode === 'fly') {
      const forward = new THREE.Vector3(0, 0, -1).applyQuaternion(this.camera.quaternion);
      this.camera.position.addScaledVector(forward, amount * 2);
    }
  }

  private pan(deltaX: number, deltaY: number): void {
    const factor = this.targetSpherical.radius * 0.0015;
    const right = new THREE.Vector3(1, 0, 0).applyQuaternion(this.camera.quaternion);
    const up = new THREE.Vector3(0, 1, 0).applyQuaternion(this.camera.quaternion);

    const panOffset = new THREE.Vector3()
      .addScaledVector(right, -deltaX * factor)
      .addScaledVector(up, deltaY * factor);

    this.target.add(panOffset);
    this.desiredTarget.add(panOffset);
  }

  public resetView(): void {
    this.targetSpherical.radius = 35;
    this.targetSpherical.phi = Math.PI / 3;
    this.targetSpherical.theta = Math.PI / 4;
  }

  public update(dt: number): void {
    if (this.transitionProgress < 1.0) {
      this.transitionProgress = Math.min(1.0, this.transitionProgress + dt / this.transitionDuration);
      // Smooth cubic easing
      const t = this.transitionProgress;
      const ease = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

      this.camera.position.lerpVectors(this.transitionStartPos, this.transitionEndPos, ease);
      this.target.lerpVectors(this.transitionStartTarget, this.transitionEndTarget, ease);
      this.camera.lookAt(this.target);

      if (this.transitionProgress >= 1.0) {
        this.updateSphericalFromCurrent();
      }
      return;
    }

    if (this.isChillMode) {
      if (!this.reduceMotion) {
        this.chillAngle += this.chillSpeed * dt;
        this.targetSpherical.theta += this.chillSpeed * dt * 0.5;
      }
    }

    if (this.currentMode === 'orbit') {
      // Damped orbit movement
      const damping = 0.08;
      this.spherical.theta = THREE.MathUtils.lerp(this.spherical.theta, this.targetSpherical.theta, damping);
      this.spherical.phi = THREE.MathUtils.lerp(this.spherical.phi, this.targetSpherical.phi, damping);
      this.spherical.radius = THREE.MathUtils.lerp(this.spherical.radius, this.targetSpherical.radius, damping);

      const offset = new THREE.Vector3().setFromSpherical(this.spherical);
      this.camera.position.copy(this.target).add(offset);

      // Clamp camera height strictly above ground level (min height 1.5)
      if (this.camera.position.y < 1.5) {
        this.camera.position.y = 1.5;
      }

      this.camera.lookAt(this.target);
    } else if (this.currentMode === 'fly') {
      // WASD / QE fly
      if (this.flyMoveVector.lengthSq() > 0) {
        const move = this.flyMoveVector.clone().normalize();
        const forward = new THREE.Vector3(0, 0, -1).applyQuaternion(this.camera.quaternion);
        const right = new THREE.Vector3(1, 0, 0).applyQuaternion(this.camera.quaternion);
        const up = new THREE.Vector3(0, 1, 0);

        const delta = new THREE.Vector3()
          .addScaledVector(forward, -move.z)
          .addScaledVector(right, move.x)
          .addScaledVector(up, move.y)
          .multiplyScalar(this.flySpeed * dt);

        this.camera.position.add(delta);
        if (this.camera.position.y < 2.0) {
          this.camera.position.y = 2.0;
        }
      }
    }
  }

  public isInteracting(): boolean {
    return this.isPointerDown;
  }

  public setAspectRatio(aspect: number): void {
    this.camera.aspect = aspect;
    this.camera.updateProjectionMatrix();
  }

  public dispose(): void {
    this.activePointers.clear();
  }
}
